import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Activity, Loader2, X, SlidersHorizontal } from "lucide-react";
import TorrentRow from "@/components/TorrentRow";
import api from "@/lib/api";
import { toast } from "sonner";
import { loadDeviceId, findDevice, pickBestRow } from "@/lib/deviceProfiles";

const DEFAULT_LIMIT = 8;
const LIVE_INTERVAL_MS = 60_000;

// localStorage key for sticky filter preferences. Bumping `v1` invalidates
// older saves if we ever change the chip schema.
const SAVED_FILTERS_KEY = "torhub:torrent-filters:v1";

// "Quality floor" prefs — set in Settings, applied invisibly before any
// chip filter so the user never sees rips below their floor.
function getQualityFloor() {
  let hideCam = true;   // default ON
  let hideSd  = false;  // default OFF
  try {
    const c = localStorage.getItem("torhub:hideCam");
    const s = localStorage.getItem("torhub:hideSd");
    if (c !== null) hideCam = c !== "0";
    if (s !== null) hideSd  = s === "1";
  } catch { /* localStorage unavailable — keep defaults */ }
  return { hideCam, hideSd };
}

function passesQualityFloor(row, floor) {
  if (floor.hideCam && row.release_source === "CAM") return false;
  if (floor.hideSd) {
    if (row.release_source === "DVDRip") return false;
    if (row.resolution === "480p" || row.resolution === "360p") return false;
    // Some sources only tag quality=SD without setting resolution/source.
    if (!row.resolution && row.quality === "SD") return false;
  }
  return true;
}

function loadSavedFilters() {
  try {
    const raw = localStorage.getItem(SAVED_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((k) => typeof k === "string"));
  } catch {
    return null;
  }
}

function persistSavedFilters(set) {
  try {
    if (!set || set.size === 0) localStorage.removeItem(SAVED_FILTERS_KEY);
    else localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(Array.from(set)));
  } catch { /* localStorage unavailable / quota — ignore silently */ }
}

// Order matters — chips appear left → right in this order. Each chip's
// `match(row)` returns true if the row should be kept when the chip is
// active. We only render a chip when at least one row would match it so
// the strip stays clean for small/obscure releases.
const FILTER_CHIPS = [
  { key: "4K",      label: "4K",      group: "res",   match: (r) => r.resolution === "4K" },
  { key: "1080p",   label: "1080p",   group: "res",   match: (r) => r.resolution === "1080p" },
  { key: "720p",    label: "720p",    group: "res",   match: (r) => r.resolution === "720p" },
  { key: "HDR",     label: "HDR",     group: "hdr",   match: (r) => !!r.hdr,                 tone: "yellow" },
  { key: "DV",      label: "Dolby Vision", group: "hdr", match: (r) => r.hdr === "DV",       tone: "blue" },
  { key: "REMUX",   label: "REMUX",   group: "src",   match: (r) => r.release_source === "REMUX",  tone: "red" },
  { key: "BluRay",  label: "BluRay",  group: "src",   match: (r) => r.release_source === "BluRay" },
  { key: "WEB-DL",  label: "WEB-DL",  group: "src",   match: (r) => r.release_source === "WEB-DL" },
  { key: "HEVC",    label: "HEVC",    group: "codec", match: (r) => r.codec === "HEVC" },
  { key: "Atmos",   label: "Atmos",   group: "audio", match: (r) => Array.isArray(r.audio) && r.audio.includes("Atmos") },
];

function chipTone(active, tone) {
  if (active) {
    if (tone === "yellow") return "bg-[var(--tt-yellow)] border-[var(--tt-yellow)] text-black shadow-[0_0_10px_-2px_rgba(255,215,0,0.55)]";
    if (tone === "red")    return "bg-red-600 border-red-600 text-white shadow-[0_0_10px_-2px_rgba(220,38,38,0.6)]";
    if (tone === "blue")   return "bg-blue-600 border-blue-600 text-white shadow-[0_0_10px_-2px_rgba(37,99,235,0.6)]";
    return "bg-[var(--tt-primary)] border-[var(--tt-primary)] text-white shadow-[0_0_10px_-2px_rgba(255,42,109,0.55)]";
  }
  return "bg-transparent border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white hover:border-white/40";
}

export default function TorrentList({ title, rows, badgeColor = "var(--tt-primary)", initialLimit = DEFAULT_LIMIT, defaultOpen = true, testId, mediaType, tmdbId, onModChange }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [lastTick, setLastTick] = useState(null);
  const [freshCounts, setFreshCounts] = useState({});
  const [activeFilters, setActiveFilters] = useState(() => loadSavedFilters() || new Set());
  const aliveRef = useRef(true);

  // Persist any change to the chip set so the user's preferred quality bar
  // (e.g. "1080p + WEB-DL + Atmos") sticks across titles and reloads.
  useEffect(() => { persistSavedFilters(activeFilters); }, [activeFilters]);

  const hashesToScrape = useMemo(() => {
    if (!rows) return [];
    return rows.map((r) => r.info_hash).filter((h) => h && h.length === 40);
  }, [rows]);

  // Resolve the user's device profile + the best-fit row for it. We pick
  // the recommendation from the *full* `rows` set (not the chip-filtered
  // set) because the user might be filtering for curiosity but still wants
  // to see the platform-correct pick highlighted.
  const device = findDevice(loadDeviceId());
  const bestPick = useMemo(
    () => pickBestRow(rows || [], device),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, device.id]
  );
  const bestHash = bestPick?.row?.info_hash || null;

  // Apply quality-floor prefs first so the chip strip / counts / "no
  // matches" logic all operate on the already-cleaned set. We re-read on
  // every render so changes made in Settings take effect without a reload.
  const floor = getQualityFloor();
  const floorRows = useMemo(() => {
    if (!rows) return [];
    if (!floor.hideCam && !floor.hideSd) return rows;
    return rows.filter((r) => passesQualityFloor(r, floor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, floor.hideCam, floor.hideSd]);
  const hiddenByFloor = (rows?.length || 0) - floorRows.length;

  // Only show chips that would actually filter something out — if 0 rows
  // (or all rows) match, the chip is useless and just adds noise. Exception:
  // a chip that's currently *active* is always shown so the user can see
  // and deactivate their saved selection even when this title has no
  // matching rows.
  const visibleChips = useMemo(() => {
    if (!floorRows) return [];
    return FILTER_CHIPS.filter((c) => {
      if (activeFilters.has(c.key)) return true;
      const n = floorRows.reduce((acc, r) => acc + (c.match(r) ? 1 : 0), 0);
      return n > 0 && n < floorRows.length;
    });
  }, [floorRows, activeFilters]);

  const toggleFilter = (key) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setShowAll(false);
  };

  const filtered = useMemo(() => {
    if (!floorRows) return [];
    if (activeFilters.size === 0) return floorRows;
    // Within a group (e.g. "1080p" + "720p" both in `res`) we OR — within
    // the same dimension the user wants either-or. Across groups we AND.
    const byGroup = new Map();
    for (const k of activeFilters) {
      const c = FILTER_CHIPS.find((x) => x.key === k);
      if (!c) continue;
      if (!byGroup.has(c.group)) byGroup.set(c.group, []);
      byGroup.get(c.group).push(c);
    }
    return floorRows.filter((r) => {
      for (const chips of byGroup.values()) {
        if (!chips.some((c) => c.match(r))) return false;
      }
      return true;
    });
  }, [floorRows, activeFilters]);

  const doScrape = async ({ silent = false } = {}) => {
    if (hashesToScrape.length === 0) return;
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.post("/torrents/scrape", { info_hashes: hashesToScrape });
      if (!aliveRef.current) return;
      setFreshCounts(data.results || {});
      setLastTick(Date.now());
      if (!silent) toast.success(`Updated ${data.found}/${data.queried} torrents from live trackers`);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Tracker scrape failed");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  // Auto-refresh every 60s while live-mode is on
  useEffect(() => {
    aliveRef.current = true;
    if (!liveMode) return;
    const id = setInterval(() => doScrape({ silent: true }), LIVE_INTERVAL_MS);
    return () => { aliveRef.current = false; clearInterval(id); };
  }, [liveMode, hashesToScrape]);

  const handleClick = async () => {
    if (refreshing) return;
    if (!liveMode) {
      setLiveMode(true);
      await doScrape();
    } else {
      setLiveMode(false);
    }
  };

  // Pin the recommended row to the top of the visible window so the user
  // always sees the "Best for your device" highlight, even if its seeder
  // count would have buried it lower down.
  const sortedFiltered = useMemo(() => {
    if (!bestHash) return filtered;
    const idx = filtered.findIndex((r) => r.info_hash === bestHash);
    if (idx <= 0) return filtered;
    const next = filtered.slice();
    const [pick] = next.splice(idx, 1);
    next.unshift(pick);
    return next;
  }, [filtered, bestHash]);

  if (!rows || rows.length === 0) return null;

  const visible = showAll ? sortedFiltered : sortedFiltered.slice(0, initialLimit);
  const hasMore = sortedFiltered.length > initialLimit;
  const filteredOut = rows.length - filtered.length;
  const decorate = (t) => {
    let row = t;
    const f = freshCounts[(t.info_hash || "").toLowerCase()];
    if (f) row = { ...row, seeders: f.seeders, leechers: f.leechers, _live: true };
    if (bestHash && t.info_hash === bestHash) {
      row = {
        ...row,
        _recommended: true,
        _recommendedFor: device.short || device.label,
        _recommendedReasons: bestPick?.reasons || [],
      };
    }
    return row;
  };

  return (
    <div className="mb-4" data-testid={testId}>
      <div className="flex items-center px-6 md:px-8 py-2 gap-2">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`${testId}-toggle`}
          className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity"
        >
          {open
            ? <ChevronDown className="w-4 h-4 text-[var(--tt-text-muted)]" />
            : <ChevronRight className="w-4 h-4 text-[var(--tt-text-muted)]" />}
          <span className="font-display font-bold text-sm" style={{ color: badgeColor }}>{title}</span>
          <span className="text-[10px] font-mono-tt bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] px-2 py-0.5 rounded">
            {activeFilters.size > 0 ? `${filtered.length}/${floorRows.length}` : floorRows.length}
            {hiddenByFloor > 0 && (
              <span
                className="ml-1 text-red-400"
                title={`${hiddenByFloor} low-quality rip${hiddenByFloor === 1 ? "" : "s"} hidden by your Quality floor (Settings)`}
              >−{hiddenByFloor}</span>
            )}
          </span>
        </button>
        {liveMode && lastTick && (
          <span className="text-[10px] text-[var(--tt-text-muted)] font-mono-tt hidden sm:inline" data-testid={`${testId}-last-tick`}>
            {Math.max(0, Math.floor((Date.now() - lastTick) / 1000))}s ago
          </span>
        )}
        {hashesToScrape.length > 0 && (
          <button
            type="button"
            onClick={handleClick}
            disabled={refreshing}
            data-testid={`${testId}-refresh-seeders`}
            title={liveMode ? "Live mode active — click to stop auto-refresh" : "Enable live tracker auto-refresh"}
            className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 disabled:opacity-50 transition-colors ${
              liveMode
                ? "bg-emerald-400/25 text-emerald-300 border border-emerald-400/50"
                : "bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-400 border border-emerald-400/30"
            }`}
          >
            {refreshing ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Scraping…</>
            ) : liveMode ? (
              <>
                <span className="relative inline-flex w-2 h-2">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
                </span>
                Live
                <X className="w-3 h-3 ml-0.5 opacity-60" />
              </>
            ) : (
              <><Activity className="w-3 h-3" /> Live seeds</>
            )}
          </button>
        )}
      </div>
      {open && visibleChips.length > 0 && (
        <div
          className="flex items-center flex-wrap gap-1.5 px-6 md:px-8 pb-3 pt-1"
          data-testid={`${testId}-filter-chips`}
        >
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--tt-text-muted)] mr-1"
            title={activeFilters.size > 0
              ? "Your filter selection is saved and will be applied automatically next time."
              : "Pick one or more chips — your selection is saved across titles."}
          >
            <SlidersHorizontal className="w-3 h-3" />
            Filter
            {activeFilters.size > 0 && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-400 normal-case tracking-normal text-[9px] font-semibold">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Saved
              </span>
            )}
          </span>
          {visibleChips.map((c) => {
            const active = activeFilters.has(c.key);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleFilter(c.key)}
                data-testid={`${testId}-filter-${c.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${chipTone(active, c.tone)}`}
                title={c.label}
              >
                {c.label}
              </button>
            );
          })}
          {activeFilters.size > 0 && (
            <button
              type="button"
              onClick={() => setActiveFilters(new Set())}
              data-testid={`${testId}-filter-clear`}
              className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white hover:border-white/40 inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      )}
      {open && (
        <>
          {filtered.length === 0 && rows.length > 0 ? (
            <div
              data-testid={`${testId}-no-matches`}
              className="px-6 md:px-8 py-6 text-center text-xs text-[var(--tt-text-muted)] border-b border-[var(--tt-border)]"
            >
              {activeFilters.size > 0
                ? <>No torrents match these filters — {filteredOut} hidden.</>
                : <>Every torrent for this title is below your Quality floor — <span className="text-red-400">{hiddenByFloor} hidden</span>. Adjust in <a href="/settings" className="underline hover:text-white">Settings</a>.</>}
            </div>
          ) : (
            visible.map((t, i) => (
              <TorrentRow key={`${t.info_hash || t.name}-${i}`} row={decorate(t)} index={i} mediaType={mediaType} tmdbId={tmdbId} onModChange={onModChange} />
            ))
          )}
          {hasMore && (
            <div className="px-6 md:px-8 pt-3 pb-1">
              <button
                onClick={() => setShowAll((s) => !s)}
                data-testid={`${testId}-show-more`}
                className="text-xs font-semibold text-[var(--tt-primary)] hover:text-[var(--tt-primary-strong)] flex items-center gap-1.5"
              >
                {showAll
                  ? <>Show less <ChevronDown className="w-3 h-3 rotate-180" /></>
                  : <>Show all {filtered.length} <ChevronDown className="w-3 h-3" /></>}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
